import { type FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Form, Row, Skeleton } from 'antd';

import { routeFileSelector } from '@/utilz';
import type { RootState } from '@/redux/RootReducer';
import SaveCancelButtons from '@/views/components/Buttons/SaveCancelButtons';
import EmptyRecords from '@/views/components/Common/EmptyRecords';
import PrimaryLabel from '@/views/components/Labels/PrimaryLabel';
import Paper from '@/views/components/Paper/Paper';

import { useGetCarrierInformationSettings, useUpdateCarrierInformationSettings } from '../hooks';
import type { CarrierInformationSettings } from '../types';
import CarrierClaimsAddressForm from './carrier-claims-address-form';

interface CarrierClaimsAddressContainerProps {
  carrierKey?: number;
}

const CarrierClaimsAddressContainer: FC<CarrierClaimsAddressContainerProps> = ({ carrierKey }) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [form] = Form.useForm<CarrierInformationSettings>();
  const { selectedCompany } = useSelector((state: RootState) => state.auth);

  const { data: carrierAddressData, isFetching: isLoading } = useGetCarrierInformationSettings({
    carrierKey,
    companyId: selectedCompany?.companyId || 0,
  });

  const { mutate: saveAddress, isPending: isSaving } = useUpdateCarrierInformationSettings({
    companyId: selectedCompany?.companyId || 0,
  });

  const handleSaveLD = async () => {
    // Prevent multiple API calls
    if (isSaving) {
      return;
    }

    try {
      // Validate only LD address fields
      await form.validateFields([
        ['ldClaimAddress', 'carrierDetailName'],
        ['ldClaimAddress', 'carrierDetailAddr1'],
        ['ldClaimAddress', 'carrierDetailCountry'],
        ['ldClaimAddress', 'carrierDetailZip'],
        ['ldClaimAddress', 'carrierDetailCity'],
        ['ldClaimAddress', 'carrierDetailState'],
        ['ldClaimAddress', 'carrierDetailPhone'],
        ['ldClaimAddress', 'carrierDetailFax'],
        ['ldClaimAddress', 'carrierEmail'],
      ]);
      const formValues = form.getFieldsValue();

      // Merge with existing data to preserve all fields, but only update LD address
      const payload: CarrierInformationSettings = {
        carrierSettings: {
          ...(carrierAddressData?.carrierSettings || {}),
          ...formValues.carrierSettings,
          carrierKey: carrierKey || 0,
        },
        ldClaimAddress: {
          ...(carrierAddressData?.ldClaimAddress || {}),
          ...formValues.ldClaimAddress,
          addressType: 'L',
          carrierDetailKey: 1, // 1 for L&D
          carrierKey: carrierKey || 0,
        },
        ocClaimAddress: {
          ...(carrierAddressData?.ocClaimAddress || {}),
          ...formValues.ocClaimAddress,
        },
      };
      saveAddress(payload);
    } catch (error) {
      // Validation failed, error is handled by Ant Design Form
      console.error('LD Address validation failed:', error);
    }
  };

  const handleSaveOC = async () => {
    // Prevent multiple API calls
    if (isSaving) {
      return;
    }

    try {
      // Validate only OC address fields
      await form.validateFields([
        ['ocClaimAddress', 'carrierDetailName'],
        ['ocClaimAddress', 'carrierDetailAddr1'],
        ['ocClaimAddress', 'carrierDetailCountry'],
        ['ocClaimAddress', 'carrierDetailZip'],
        ['ocClaimAddress', 'carrierDetailCity'],
        ['ocClaimAddress', 'carrierDetailState'],
        ['ocClaimAddress', 'carrierDetailPhone'],
        ['ocClaimAddress', 'carrierDetailFax'],
        ['ocClaimAddress', 'carrierEmail'],
      ]);
      const formValues = form.getFieldsValue();

      // Merge with existing data to preserve all fields, but only update OC address
      const payload: CarrierInformationSettings = {
        carrierSettings: {
          ...(carrierAddressData?.carrierSettings || {}),
          ...formValues.carrierSettings,
          carrierKey: carrierKey || 0,
        },
        ldClaimAddress: {
          ...(carrierAddressData?.ldClaimAddress || {}),
          ...formValues.ldClaimAddress,
        },
        ocClaimAddress: {
          ...(carrierAddressData?.ocClaimAddress || {}),
          ...formValues.ocClaimAddress,
          addressType: 'O',
          carrierDetailKey: 2, // 2 for OC
          carrierKey: carrierKey || 0,
        },
      };
      saveAddress(payload);
    } catch (error) {
      // Validation failed, error is handled by Ant Design Form
      console.error('OC Address validation failed:', error);
    }
  };

  const resetLD = () => {
    if (carrierAddressData?.ldClaimAddress) {
      form.setFieldsValue({
        ldClaimAddress: carrierAddressData.ldClaimAddress,
      });
    }
  };

  const resetOC = () => {
    if (carrierAddressData?.ocClaimAddress) {
      form.setFieldsValue({
        ocClaimAddress: carrierAddressData.ocClaimAddress,
      });
    }
  };

  useEffect(() => {
    if (carrierAddressData) {
      // Set default values for country and state if not present
      const formData = { ...carrierAddressData };
      if (formData.ldClaimAddress) {
        if (!formData.ldClaimAddress.carrierDetailCountry) {
          formData.ldClaimAddress.carrierDetailCountry = 'US';
        }
        if (!formData.ldClaimAddress.carrierDetailState) {
          formData.ldClaimAddress.carrierDetailState = 'GA';
        }
        if (carrierKey && !formData.ldClaimAddress.carrierKey) {
          formData.ldClaimAddress.carrierKey = carrierKey;
        }
      }

      if (formData.ocClaimAddress) {
        if (!formData.ocClaimAddress.carrierDetailCountry) {
          formData.ocClaimAddress.carrierDetailCountry = 'US';
        }
        if (!formData.ocClaimAddress.carrierDetailState) {
          formData.ocClaimAddress.carrierDetailState = 'GA';
        }
        if (carrierKey && !formData.ocClaimAddress.carrierKey) {
          formData.ocClaimAddress.carrierKey = carrierKey;
        }
      }

      form.setFieldsValue(formData);
    } else if (carrierKey) {
      // If no data but carrier is selected, set initial defaults
      form.setFieldsValue({
        carrierSettings: {
          carrierKey,
        } as Partial<CarrierInformationSettings['carrierSettings']>,
        ldClaimAddress: {
          carrierDetailCountry: 'US',
          carrierDetailState: 'GA',
          carrierKey,
        } as Partial<CarrierInformationSettings['ldClaimAddress']>,
        ocClaimAddress: {
          carrierDetailCountry: 'US',
          carrierDetailState: 'GA',
          carrierKey,
        } as Partial<CarrierInformationSettings['ocClaimAddress']>,
      } as Partial<CarrierInformationSettings>);
    }
  }, [carrierAddressData, carrierKey, form]);

  return (
    <div className="h-100">
      <Form<CarrierInformationSettings> form={form}>
        {isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
        {!isLoading &&
          (carrierKey ? (
            <Row gutter={24} className="h-100">
              <Col md={12} className="d-flex flex-direction-column">
                <Paper className="d-flex flex-direction-column flex-nowrap flex-1" border>
                  <div className="px-20 py-15 border-bottom-border d-flex justify-content-between align-items-center bg-primary-light">
                    <PrimaryLabel className="mb-0 fw-600">Loss and Damage Claim Address</PrimaryLabel>
                    {Boolean(carrierKey) && (
                      <SaveCancelButtons
                        onCancel={resetLD}
                        onSave={handleSaveLD}
                        cancelText={t('translation:default.buttons.restoreToDefault')}
                      />
                    )}
                  </div>
                  <div className="px-20 pt-20 pb-20 flex-1 custom-scroll-bar">
                    <CarrierClaimsAddressForm addressType="ldClaimAddress" form={form} />
                  </div>
                </Paper>
              </Col>
              <Col md={12} className="d-flex flex-direction-column">
                <Paper className="d-flex flex-direction-column flex-nowrap flex-1" border>
                  <div className="px-20 py-15 border-bottom-border d-flex justify-content-between align-items-center bg-primary-light">
                    <PrimaryLabel className="mb-0 fw-600">Overcharge Claim Address</PrimaryLabel>
                    {Boolean(carrierKey) && (
                      <SaveCancelButtons
                        onCancel={resetOC}
                        onSave={handleSaveOC}
                        cancelText={t('translation:default.buttons.restoreToDefault')}
                      />
                    )}
                  </div>
                  <div className="px-20 pt-20 pb-20 flex-1 custom-scroll-bar">
                    <CarrierClaimsAddressForm addressType="ocClaimAddress" form={form} />
                  </div>
                </Paper>
              </Col>
            </Row>
          ) : (
            <EmptyRecords descriptionOne="" descriptionTwo="" title="Please select a carrier" />
          ))}
      </Form>
    </div>
  );
};

export default CarrierClaimsAddressContainer;
